import {
  ChangeDetectionStrategy,
  Component,
  computed,
  effect,
  input,
  output,
  signal,
  untracked,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormControl } from '@angular/forms';

import { COMMON_IMPORTS } from '../../../../../Shared/common-imports';
import { ReportPickerOption } from '../../../Interfaces/report-picker-option.interface';
import { ReportPickerState } from '../../../Types/report-picker-state.type';

@Component({
  selector: 'app-all-demands-picker',
  standalone: true,
  imports: [COMMON_IMPORTS],
  changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: './all-demands-picker.component.html',
  styleUrl: './all-demands-picker.component.scss',
})
export class AllDemandsPickerComponent {
  readonly label = input.required<string>();
  readonly options = input.required<ReadonlyArray<ReportPickerOption>>();
  readonly selectedId = input<string | null>(null);
  readonly state = input<ReportPickerState>('idle');
  readonly hint = input<string | null>(null);

  readonly selectedIdChange = output<string | null>();

  readonly queryControl = new FormControl<string | ReportPickerOption | null>('');

  private readonly _filterText = signal('');

  readonly selectedOption = computed<ReportPickerOption | null>(
    () => this.options().find((option) => option.id === this.selectedId()) ?? null,
  );

  private readonly _selectionKey = computed(() => {
    const selected = this.selectedOption();
    return selected ? `${selected.id}:${selected.label}` : '';
  });

  readonly visibleOptions = computed<ReportPickerOption[]>(() => {
    const query = this._filterText().trim().toLowerCase();
    if (!query) return [...this.options()];
    return this.options().filter((option) =>
      `${option.label} ${option.detail ?? ''}`.toLowerCase().includes(query),
    );
  });

  readonly placeholderKey = computed<string | null>(() => {
    const state = this.state();
    if (state === 'loading') return 'app.reports.allDemands.filters.pickerLoading';
    if (state === 'error') return 'app.reports.allDemands.filters.pickerLoadFailed';
    if (state === 'ready' && this.visibleOptions().length === 0) {
      return 'app.reports.allDemands.filters.pickerNoMatches';
    }
    return null;
  });

  readonly displayOption = (option: ReportPickerOption | string | null): string =>
    typeof option === 'string' ? option : (option?.label ?? '');

  constructor() {
    this._registerSelectionSync();
    this._registerQueryBinding();
  }

  private _registerSelectionSync(): void {
    effect(() => {
      this._selectionKey();
      untracked(() => {
        this._filterText.set('');
        this.queryControl.setValue(this.selectedOption() ?? '', { emitEvent: false });
      });
    });
  }

  private _registerQueryBinding(): void {
    this.queryControl.valueChanges
      .pipe(takeUntilDestroyed())
      .subscribe((value) => this._filterText.set(typeof value === 'string' ? value : ''));
  }

  onOptionSelected(option: ReportPickerOption | null): void {
    this.selectedIdChange.emit(option?.id ?? null);
  }

  onPanelClosed(): void {
    const typed = this.queryControl.value;
    if (typeof typed === 'string') {
      const cleared = typed.trim().length === 0 && this.selectedId() !== null;
      if (cleared) this.selectedIdChange.emit(null);
      this.queryControl.setValue(this.selectedOption() ?? '', { emitEvent: false });
      this._filterText.set('');
    }
  }

  onClear(): void {
    this.queryControl.setValue('', { emitEvent: false });
    this._filterText.set('');
    if (this.selectedId() !== null) this.selectedIdChange.emit(null);
  }
}
